import React, { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import Popover from 'shared/components/Popover';
import { cn } from 'shared/lib/utils';

export interface FormMultiSelectOption {
  value: string;
  label: string;
}

interface FormMultiSelectProps {
  label: string;
  options: FormMultiSelectOption[];
  value: string[];
  onChange: (next: string[]) => void;
  error?: string;
  hint?: string;
  required?: boolean;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  id?: string;
  name?: string;
}

/**
 * Multi-select dropdown styled to match FormSelect (`.input-modern` shell +
 * ChevronDown affordance). Looks like a single-select to keep the form
 * visually consistent, but accepts multiple values via a checklist panel.
 *
 * Pure DOM + Tailwind — no external dependency, RTL-aware via `ltr:`/`rtl:`
 * utilities so the chevron / panel placement work in both directions.
 */
const FormMultiSelect: React.FC<FormMultiSelectProps> = ({
  label,
  options,
  value,
  onChange,
  error,
  hint,
  required,
  placeholder,
  disabled,
  className,
  id,
  name,
}) => {
  const { t } = useTranslation();
  const inputId =
    id || `field-${label.replace(/\s+/g, '-').toLowerCase()}-multiselect`;
  const errorMessage = error
    ? (t(`validation.${error}`, { defaultValue: error }) as string)
    : undefined;

  const containerRef = useRef<HTMLDivElement>(null);
  const fieldRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        !containerRef.current?.contains(target) &&
        !panelRef.current?.contains(target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  const toggle = (optionValue: string) => {
    if (value.includes(optionValue)) {
      onChange(value.filter((v) => v !== optionValue));
    } else {
      onChange([...value, optionValue]);
    }
  };

  const selectedLabels = options
    .filter((o) => value.includes(o.value))
    .map((o) => o.label);

  const displayText = selectedLabels.length > 0
    ? selectedLabels.join(', ')
    : placeholder ?? '';

  return (
    <div
      className="flex flex-col gap-2 w-full"
      ref={containerRef}
      data-error={error ? 'true' : undefined}
    >
      <label htmlFor={inputId} className="text-base font-medium text-gray-700">
        {label}
        {required && <span className="text-gray-500 ms-1">*</span>}
      </label>
      <div className="relative" ref={fieldRef}>
        <button
          type="button"
          id={inputId}
          name={name}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-invalid={!!error}
          disabled={disabled}
          onClick={() => !disabled && setIsOpen((o) => !o)}
          className={cn(
            'input-modern appearance-none pe-10 text-start',
            error && 'has-error',
            disabled && 'opacity-60 cursor-not-allowed',
            selectedLabels.length === 0 && 'text-gray-400',
            className,
          )}
        >
          <span className="block truncate">{displayText}</span>
        </button>
        <ChevronDown
          size={18}
          className={cn(
            'pointer-events-none absolute end-3 top-1/2 -translate-y-1/2 text-gray-400 transition-transform',
            isOpen && 'rotate-180',
          )}
        />

        <Popover
          anchorRef={fieldRef}
          open={isOpen && !disabled}
          panelRef={panelRef}
          className="rounded-xl border border-gray-200 bg-white shadow-lg"
        >
          <ul role="listbox" aria-multiselectable className="py-1">
            {options.map((opt) => {
              const isSelected = value.includes(opt.value);
              return (
                <li
                  key={opt.value}
                  role="option"
                  aria-selected={isSelected}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    toggle(opt.value);
                  }}
                  className={cn(
                    'flex items-center gap-3 cursor-pointer px-3 py-2.5 text-base hover:bg-gray-50',
                    isSelected && 'bg-gray-100',
                  )}
                >
                  <span
                    className={cn(
                      'w-4 h-4 rounded border flex items-center justify-center flex-shrink-0',
                      isSelected
                        ? 'bg-gray-900 border-gray-900 text-white'
                        : 'bg-white border-gray-300',
                    )}
                  >
                    {isSelected && <Check size={12} strokeWidth={3} />}
                  </span>
                  <span className="text-gray-700">{opt.label}</span>
                </li>
              );
            })}
          </ul>
        </Popover>
      </div>
      {errorMessage ? (
        <span className="text-xs font-medium text-red-600">{errorMessage}</span>
      ) : hint ? (
        <span className="text-xs text-gray-500">{hint}</span>
      ) : null}
    </div>
  );
};

export default FormMultiSelect;
